import React from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DifferenceIcon from "@mui/icons-material/Difference";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import PublishIcon from "@mui/icons-material/Publish";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TransformIcon from "@mui/icons-material/Transform";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useLocation } from "react-router-dom";

import {
  mergeGuidedIntoDestination,
  parseGuidedTask,
  stripGuidedParams,
} from "./trainingPlansV2GuidedTaskParams";
import {
  getCommonTaskFlowById,
  getGuidedStepDestination,
} from "./trainingPlansV2CommonTaskFlows";
import { renderFlowStepBody } from "./trainingPlansV2RenderStepBody";
import { trackTrainingPlansV2Event } from "./services/trainingPlansV2Service";

const FLOW_ICONS = {
  PlaylistAddCheck: PlaylistAddCheckIcon,
  Difference: DifferenceIcon,
  GroupAdd: GroupAddIcon,
  Publish: PublishIcon,
  ContentCopy: ContentCopyIcon,
  UploadFile: UploadFileIcon,
  AltRoute: AltRouteIcon,
  Transform: TransformIcon,
  SwapHoriz: SwapHorizIcon,
};

const TrainingPlansV2GuidedTaskBar = ({ navigateWithStagingGuard }) => {
  const location = useLocation();
  const [expanded, setExpanded] = React.useState(true);

  const guided = React.useMemo(() => parseGuidedTask(location.search), [location.search]);
  const flow = guided ? getCommonTaskFlowById(guided.flowId) : null;

  const stepIndex = React.useMemo(() => {
    if (!flow || !guided) return 0;
    return Math.min(Math.max(0, guided.stepIndex), flow.steps.length - 1);
  }, [flow, guided]);

  React.useEffect(() => {
    if (!flow || !guided) return;
    if (guided.stepIndex === stepIndex) return;
    const dest = getGuidedStepDestination(flow, stepIndex, location, location.search);
    const current = `${location.pathname}${location.search}`;
    if (dest && dest !== current) {
      navigateWithStagingGuard(dest, { replace: true });
    }
  }, [flow, guided, stepIndex, location.pathname, location.search, navigateWithStagingGuard]);

  React.useEffect(() => {
    if (!guided || !flow) return;
    trackTrainingPlansV2Event("common_tasks_guided_step", {
      flowId: flow.id,
      stepIndex,
    });
  }, [flow?.id, guided?.flowId, stepIndex]);

  const exitGuided = React.useCallback(() => {
    const qs = stripGuidedParams(location.search);
    const next = `${location.pathname}${qs}`;
    if (flow) {
      trackTrainingPlansV2Event("common_tasks_guided_exit", {
        flowId: flow.id,
        stepIndex,
      });
    }
    navigateWithStagingGuard(next);
  }, [flow, location.pathname, location.search, navigateWithStagingGuard, stepIndex]);

  const goNext = React.useCallback(() => {
    if (!flow) return;
    const max = flow.steps.length - 1;
    if (stepIndex >= max) {
      trackTrainingPlansV2Event("common_tasks_guided_done", { flowId: flow.id });
      const qs = stripGuidedParams(location.search);
      navigateWithStagingGuard(`${location.pathname}${qs}`);
      return;
    }
    const dest = getGuidedStepDestination(flow, stepIndex + 1, location, location.search);
    if (dest) navigateWithStagingGuard(dest);
  }, [flow, location, navigateWithStagingGuard, stepIndex]);

  const goBack = React.useCallback(() => {
    if (!flow || stepIndex <= 0) return;
    const dest = getGuidedStepDestination(flow, stepIndex - 1, location, location.search);
    if (dest) navigateWithStagingGuard(dest);
  }, [flow, location, navigateWithStagingGuard, stepIndex]);

  const runShortcut = React.useCallback(
    (shortcut) => {
      if (!flow) return;
      const sp = new URLSearchParams();
      Object.entries(shortcut.search || {}).forEach(([k, v]) => {
        if (v != null && v !== "") sp.set(k, String(v));
      });
      const dest = mergeGuidedIntoDestination(
        shortcut.path,
        sp.toString(),
        location.search,
        { flowId: flow.id, stepIndex: stepIndex + 1 },
      );
      navigateWithStagingGuard(dest);
    },
    [flow, location.search, navigateWithStagingGuard, stepIndex],
  );

  if (!guided || !flow) return null;

  const step = flow.steps[stepIndex];
  const IconComponent = FLOW_ICONS[flow.icon] || HomeWorkIcon;
  const maxSteps = flow.steps.length;
  const progress = maxSteps > 0 ? ((stepIndex + 1) / maxSteps) * 100 : 0;
  const isLast = stepIndex >= maxSteps - 1;

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        borderTop: 1,
        borderColor: "divider",
        borderRadius: 0,
        maxHeight: expanded ? "min(55vh, 420px)" : 56,
        display: "flex",
        flexDirection: "column",
        transition: "max-height 0.2s ease",
      }}
    >
      <LinearProgress variant="determinate" value={progress} sx={{ height: 3 }} />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1, py: 0.5, borderBottom: expanded ? 1 : 0, borderColor: "divider" }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <IconComponent color="primary" sx={{ fontSize: 28, flexShrink: 0 }} />
          <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ maxWidth: { xs: 160, sm: 360 } }}>
            {flow.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {stepIndex + 1}/{maxSteps}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button size="small" onClick={() => setExpanded((e) => !e)} sx={{ textTransform: "none" }}>
            {expanded ? "Minimize" : "Expand"}
          </Button>
          <IconButton size="small" aria-label="Exit guided task" onClick={exitGuided}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      <Collapse in={expanded}>
        <Box sx={{ overflow: "auto", px: 2, py: 1.5, flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {step.title}
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
            {renderFlowStepBody(step.body)}
          </Typography>
          {step.shortcut ? (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => runShortcut(step.shortcut)}
                sx={{ textTransform: "none" }}
              >
                {step.shortcut.label}
              </Button>
            </Box>
          ) : null}
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }} alignItems="center">
            <Button
              size="small"
              startIcon={<KeyboardArrowLeft />}
              onClick={goBack}
              disabled={stepIndex <= 0}
              sx={{ textTransform: "none" }}
            >
              Back
            </Button>
            <Button
              size="small"
              variant="contained"
              endIcon={isLast ? null : <KeyboardArrowRight />}
              onClick={goNext}
              sx={{ textTransform: "none" }}
            >
              {isLast ? "Done" : "Next step"}
            </Button>
            <Button size="small" onClick={exitGuided} sx={{ textTransform: "none" }}>
              Exit guide
            </Button>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default TrainingPlansV2GuidedTaskBar;
